import React, { useState, Fragment } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
const ObligationExample = () => {
  const [step, setStep] = useState(1);
  const [exampleType, setExampleType] = useState('attribute');
  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);
  const reset = () => {
    setStep(1);
    setExampleType('attribute');
  };
  // Example data
  const attributeExample = {
    name: 'Pool Maintenance Inspection',
    description: 'Mandatory safety inspection for all swimming pools',
    attributeId: 1,
    attributeName: 'Pool',
    dueDate: '2023-07-15',
    recurring: 'Quarterly'
  };
  const locationExample = {
    name: 'Fire Safety Certificate',
    description: 'Annual fire safety certification required for all NYC properties',
    location: 'New York City',
    dueDate: '2023-08-30',
    recurring: 'Annually'
  };
  const currentExample = exampleType === 'attribute' ? attributeExample : locationExample;
  return <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-134">
      <div className="bg-blue-50 px-6 py-4 border-b border-blue-100" data-id="element-135">
        <h3 className="text-lg font-medium text-blue-900" data-id="element-136">
          Obligation Creation Example
        </h3>
        <p className="mt-1 text-sm text-blue-700" data-id="element-137">
          See how to create{' '}
          {exampleType === 'attribute' ? 'an attribute-based' : 'a location-based'}{' '}
          obligation
        </p>
      </div>
      <div className="px-6 py-4" data-id="element-138">
        {/* Step indicator */}
        <div className="flex items-center mb-6" data-id="element-139">
          <div className="flex-1 flex" data-id="element-140">
            {[1, 2, 3].map(i => <Fragment key={i} data-id="element-141">
                <div className="flex items-center" data-id="element-142">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${step >= i ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-143">
                    {i}
                  </div>
                  <div className="ml-2 text-sm font-medium text-gray-700" data-id="element-144">
                    {i === 1 ? 'Type' : i === 2 ? 'Details' : 'Review'}
                  </div>
                </div>
                {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > i ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-145" />}
              </Fragment>)}
          </div>
        </div>
        {/* Step 1: Choose type */}
        {step === 1 && <div className="space-y-6" data-id="element-146">
            <div data-id="element-147">
              <h4 className="text-base font-medium text-gray-900 mb-2" data-id="element-148">
                Select Obligation Type
              </h4>
              <p className="text-sm text-gray-500 mb-4" data-id="element-149">
                Choose whether this obligation is associated with a property
                attribute or a geographic location.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-150">
                <div className={`border rounded-lg p-4 cursor-pointer ${exampleType === 'attribute' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => setExampleType('attribute')} data-id="element-151">
                  <div className="flex justify-between items-start" data-id="element-152">
                    <div data-id="element-153">
                      <h5 className="font-medium" data-id="element-154">Property Attribute</h5>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-155">
                        Link obligation to a specific property feature (pool,
                        elevator, etc.)
                      </p>
                    </div>
                    {exampleType === 'attribute' && <div className="h-5 w-5 text-blue-600" data-id="element-156">
                        <CheckIcon size={20} data-id="element-157" />
                      </div>}
                  </div>
                </div>
                <div className={`border rounded-lg p-4 cursor-pointer ${exampleType === 'location' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => setExampleType('location')} data-id="element-158">
                  <div className="flex justify-between items-start" data-id="element-159">
                    <div data-id="element-160">
                      <h5 className="font-medium" data-id="element-161">Geographic Location</h5>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-162">
                        Link obligation to a location (city, state, country)
                      </p>
                    </div>
                    {exampleType === 'location' && <div className="h-5 w-5 text-blue-600" data-id="element-163">
                        <CheckIcon size={20} data-id="element-164" />
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Step 2: Enter details */}
        {step === 2 && <div className="space-y-4" data-id="element-165">
            <h4 className="text-base font-medium text-gray-900 mb-2" data-id="element-166">
              Enter Obligation Details
            </h4>
            <div className="space-y-4" data-id="element-167">
              <div data-id="element-168">
                <label className="block text-sm font-medium text-gray-700" data-id="element-169">
                  Obligation Name
                </label>
                <div className="mt-1 p-2 border rounded-md bg-gray-50" data-id="element-170">
                  {currentExample.name}
                </div>
              </div>
              <div data-id="element-171">
                <label className="block text-sm font-medium text-gray-700" data-id="element-172">
                  Description
                </label>
                <div className="mt-1 p-2 border rounded-md bg-gray-50" data-id="element-173">
                  {currentExample.description}
                </div>
              </div>
              {exampleType === 'attribute' ? <div data-id="element-174">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-175">
                    Related Attribute
                  </label>
                  <div className="mt-1 p-2 border rounded-md bg-gray-50 flex items-center" data-id="element-176">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2" data-id="element-177">
                      {currentExample.attributeName}
                    </span>
                    (5 items)
                  </div>
                </div> : <div data-id="element-178">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-179">
                    Geographic Location
                  </label>
                  <div className="mt-1 p-2 border rounded-md bg-gray-50" data-id="element-180">
                    {currentExample.location}
                  </div>
                </div>}
              <div className="grid grid-cols-2 gap-4" data-id="element-181">
                <div data-id="element-182">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-183">
                    Due Date
                  </label>
                  <div className="mt-1 p-2 border rounded-md bg-gray-50" data-id="element-184">
                    {currentExample.dueDate}
                  </div>
                </div>
                <div data-id="element-185">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-186">
                    Recurring
                  </label>
                  <div className="mt-1 p-2 border rounded-md bg-gray-50" data-id="element-187">
                    {currentExample.recurring}
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Step 3: Review */}
        {step === 3 && <div data-id="element-188">
            <div className="mb-4" data-id="element-189">
              <h4 className="text-base font-medium text-gray-900 mb-2" data-id="element-190">
                Review Obligation
              </h4>
              <p className="text-sm text-gray-500" data-id="element-191">
                Your new obligation will appear as follows:
              </p>
            </div>
            <div className="border rounded-lg p-4 bg-white shadow-sm" data-id="element-192">
              <div className="flex justify-between" data-id="element-193">
                <h3 className="font-medium text-gray-900" data-id="element-194">
                  {currentExample.name}
                </h3>
                <button className="text-gray-400 hover:text-gray-500" data-id="element-195">
                  <span className="sr-only" data-id="element-196">Edit</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-197">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" data-id="element-198" />
                  </svg>
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500" data-id="element-199">
                {currentExample.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-200">
                {exampleType === 'attribute' ? <span className="inline-flex items-center text-xs text-gray-500" data-id="element-201">
                    <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-202">
                      <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" data-id="element-203" />
                    </svg>
                    {currentExample.attributeName}
                  </span> : <span className="inline-flex items-center text-xs text-gray-500" data-id="element-204">
                    <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-205">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" data-id="element-206" />
                    </svg>
                    {currentExample.location}
                  </span>}
                <span className="inline-flex items-center text-xs text-gray-500" data-id="element-207">
                  <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-208">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" data-id="element-209" />
                  </svg>
                  Due: {currentExample.dueDate}
                </span>
                <span className="inline-flex items-center text-xs text-gray-500" data-id="element-210">
                  <svg className="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" data-id="element-211">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" data-id="element-212" />
                  </svg>
                  {currentExample.recurring}
                </span>
              </div>
            </div>
            <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-md" data-id="element-213">
              <div className="flex" data-id="element-214">
                <div className="flex-shrink-0" data-id="element-215">
                  <CheckIcon className="h-5 w-5 text-green-400" data-id="element-216" />
                </div>
                <div className="ml-3" data-id="element-217">
                  <h3 className="text-sm font-medium text-green-800" data-id="element-218">
                    Obligation Created Successfully
                  </h3>
                  <div className="mt-2 text-sm text-green-700" data-id="element-219">
                    <p data-id="element-220">
                      The obligation has been added to your property settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {/* Navigation buttons */}
        <div className="mt-6 flex justify-between" data-id="element-221">
          {step > 1 ? <button onClick={prevStep} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-222">
              Back
            </button> : <div data-id="element-223"></div>}
          {step < 3 ? <button onClick={nextStep} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-224">
              Continue
            </button> : <button onClick={reset} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-225">
              Start Over
            </button>}
        </div>
      </div>
    </div>;
};
export default ObligationExample;